import { authorizedFetch } from "../auth/auth.ts";
import { config } from "../config.ts";

/**
 * Entrée de /store-api/book-reader/v1/bookshelf. Contrat de référence : ce que lit l'app mobile
 * de production (RemoteBook.kt), pas le code du dépôt web.
 */
export interface ShopBook {
  id: string;
  title: string;
  productId: string;
  isSmartbook: boolean;
  protection: "lcp" | "adobe" | "watermark" | null;
  format: unknown;
  isExtract: boolean;
  coverImage: string | null;
  creators: Array<{ displayName: string }>;
}

export type Readability = "web" | "app-only" | "unsupported";

export function readability(book: ShopBook): Readability {
  if (book.protection === "lcp" || book.protection === "adobe") return "app-only";
  if (typeof book.format !== "string" || book.format.toLowerCase() !== "epub") return "unsupported";
  return "web";
}

const MAX_PAGES = 50;

export async function fetchLibrary(): Promise<ShopBook[]> {
  const books: ShopBook[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const response = await authorizedFetch(`${config.shopUrl}/store-api/book-reader/v1/bookshelf?p=${page}`, {
      headers: { "sw-access-key": config.shopAccessKey, Accept: "application/json" },
      // L'adresse est la même pour tous les comptes (le client n'est que dans le jeton) : sans
      // cela, le navigateur peut resservir la bibliothèque du compte précédent après un
      // changement de compte.
      cache: "no-store",
    });
    if (!response.ok) throw new Error(`Bibliothèque indisponible (HTTP ${response.status}).`);
    const body = (await response.json()) as { data: ShopBook[]; total: number };
    books.push(...body.data);
    if (body.data.length === 0 || books.length >= body.total) break;
  }
  return books;
}

export interface ReadingSession {
  manifestUrl: string;
  title: string;
  productId: string;
  isSmartbook: boolean;
  accessMode: "full" | "excerpt";
}

export class SessionError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "SessionError";
  }
}

/** Demande au serveur de publications d'ouvrir un livre de la bibliothèque. */
export async function openReadingSession(bookId: string): Promise<ReadingSession> {
  const response = await authorizedFetch(`${config.readerApiUrl}/api/sessions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bookId }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new SessionError(body.code ?? "error", body.message ?? "Impossible d'ouvrir ce livre.");
  }
  return body as ReadingSession;
}

/** Minuscules sans accents, pour une recherche tolérante (« emile » trouve « Émile »). */
function fold(text: string): string {
  return text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

/** Recherche rapide : chaque mot saisi doit figurer dans le titre ou un auteur. */
export function matchesSearch(book: ShopBook, query: string): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = fold([book.title, ...book.creators.map((c) => c.displayName)].join(" "));
  return words.every((word) => haystack.includes(word));
}
